import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, ExternalLink, MessageSquareText, UserCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/Ui";
import { api, errMsg } from "@/lib/api";
import { money, fmtDate, fmtTime, fmtDateTime, statusStyle, METHOD_LABEL } from "@/lib/format";

export function BookingsTable({ list, reload }) {
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-2">
      {list.map((b) => (
        <div key={b.id} className="card-soft flex flex-col gap-3 p-4 md:flex-row md:items-center" data-testid={`booking-row-${b.code}`}>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2"><span className="font-mono font-bold tracking-wider">{b.code}</span><StatusBadge status={b.status} cls={statusStyle[b.status]} /><span className="chip bg-slate-100 capitalize text-slate-600">{b.channel}</span></div>
            <div className="text-sm font-semibold">{b.trip.origin_city} → {b.trip.destination_city} · {fmtDate(b.trip.departure)} {fmtTime(b.trip.departure)}</div>
            <div className="text-xs text-slate-500">{b.passengers.map((p) => `${p.seat}: ${p.name} (${p.phone})`).join(" · ")}</div>
          </div>
          <div className="text-right text-sm"><div className="font-bold">{money(b.total)}</div><div className="text-xs text-slate-500">{b.payment_status} · {METHOD_LABEL[b.payment_method]}</div></div>
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" onClick={() => window.open(`/ticket/${b.code}`, "_blank")} data-testid={`booking-view-${b.code}`}><ExternalLink className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => act(() => api.post(`/bookings/${b.code}/sms`), "SMS resent")} data-testid={`booking-sms-${b.code}`}><MessageSquareText className="h-4 w-4" /></Button>
            {b.status === "confirmed" && <Button size="icon" variant="ghost" onClick={() => act(() => api.post(`/bookings/${b.code}/board`, {}), "Boarded")} data-testid={`booking-board-${b.code}`}><UserCheck className="h-4 w-4 text-sky-600" /></Button>}
            {b.status === "cancelled" && b.cancel_reason && <span className="max-w-[220px] self-center text-xs text-rose-600" data-testid={`booking-cancel-info-${b.code}`}>{b.cancel_reason}{b.refund_amount ? ` · refund ${money(b.refund_amount)}` : ""}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

export function BookingsAdmin() {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const load = (query = q) => api.get("/admin/bookings", { params: { q: query } }).then((r) => setList(r.data));
  useEffect(() => { load(""); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-4" data-testid="bookings-admin">
      <h2 className="font-display text-2xl font-extrabold tracking-tight">Bookings</h2>
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by booking code, passenger name or phone" data-testid="bookings-search-input" />
        <Button type="submit" className="bg-[#0052FF] hover:bg-[#0041CC]" data-testid="bookings-search-button"><Search className="h-4 w-4" /></Button>
      </form>
      <BookingsTable list={list} reload={load} />
      {!list.length && <p className="text-sm text-slate-500">No bookings found.</p>}
    </div>
  );
}

export function SmsLog() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/sms").then((r) => setList(r.data)); }, []);
  return (
    <div className="space-y-4" data-testid="sms-log">
      <div>
        <h2 className="font-display text-2xl font-extrabold tracking-tight">SMS outbox</h2>
        <p className="text-sm text-slate-500">Messages sent via Hubtel SMS. Status "sent (simulated)" means Hubtel SMS keys are not added yet, so nothing was delivered.</p>
      </div>
      <div className="card-soft divide-y divide-slate-100">
        {list.map((s) => (
          <div key={s.id} className="p-4 text-sm" data-testid={`sms-row-${s.id}`}>
            <div className="flex justify-between"><b>{s.to}</b><span className="text-xs text-slate-500">{fmtDateTime(s.created_at)} · {s.status}</span></div>
            <div className="mt-1 text-slate-600">{s.message}</div>
          </div>
        ))}
        {!list.length && <p className="p-6 text-center text-sm text-slate-500">No messages yet.</p>}
      </div>
    </div>
  );
}
